import { POSITIONS } from "./constants";
import {
  Alignment,
  Axis,
  Coordinates,
  ElementRects,
  Length,
  Position,
  Rects,
  Side,
} from "./types";

/**
 * Возвращает доступные для отрисовки флайаута позиции вокруг элемента
 *
 * @return
 * available: true // расположение не приведёт к появлению скроллов
 * verticalScrollOnly: true // расположение приведёт к появлению вертикального скролла, но не приведёт к появлению горизонтального
 */
export const getAvailablePositions = ({
  flyoutRect,
  parentRect,
  containerRect,
}: Rects): Record<
  Position,
  { available: boolean; verticalScrollOnly: boolean }
> => {
  const commonTopCondition =
    parentRect.top > containerRect.top &&
    parentRect.top - containerRect.top >= flyoutRect.height;

  const commonRightCondition =
    containerRect.right > parentRect.right &&
    containerRect.right - parentRect.right >= flyoutRect.width;

  const commonBottomCondition =
    containerRect.bottom > parentRect.bottom &&
    containerRect.bottom - parentRect.bottom >= flyoutRect.height;

  const commonLeftCondition =
    parentRect.left > containerRect.left &&
    parentRect.left - containerRect.left >= flyoutRect.width;

  const horizontalStartCondition =
    containerRect.bottom > parentRect.top &&
    containerRect.bottom - parentRect.top >= flyoutRect.height;

  const parentVerticalCenter = parentRect.top + parentRect.height / 2;

  const horizontalCenterCondition =
    parentVerticalCenter > containerRect.top &&
    parentVerticalCenter - containerRect.top >= flyoutRect.height / 2 &&
    containerRect.bottom > parentVerticalCenter &&
    containerRect.bottom - parentVerticalCenter >= flyoutRect.height / 2;

  const horizontalEndCondition =
    parentRect.bottom > containerRect.top &&
    parentRect.bottom - containerRect.top >= flyoutRect.height;

  const verticalStartCondition =
    containerRect.right > parentRect.left &&
    containerRect.right - parentRect.left >= flyoutRect.width;

  const parentHorizontalCenter = parentRect.left + parentRect.width / 2;

  const verticalCenterCondition =
    parentHorizontalCenter > containerRect.left &&
    parentHorizontalCenter - containerRect.left >= flyoutRect.width / 2 &&
    containerRect.right > parentHorizontalCenter &&
    containerRect.right - parentHorizontalCenter >= flyoutRect.width / 2;

  const verticalEndCondition =
    parentRect.right > containerRect.left &&
    parentRect.right - containerRect.left >= flyoutRect.width;

  return {
    [POSITIONS.TOP_START]: {
      available: commonTopCondition && verticalStartCondition,
      verticalScrollOnly: verticalStartCondition,
    },
    [POSITIONS.TOP]: {
      available: commonTopCondition && verticalCenterCondition,
      verticalScrollOnly: verticalCenterCondition,
    },
    [POSITIONS.TOP_END]: {
      available: commonTopCondition && verticalEndCondition,
      verticalScrollOnly: verticalEndCondition,
    },
    [POSITIONS.RIGHT_START]: {
      available: commonRightCondition && horizontalStartCondition,
      verticalScrollOnly: horizontalStartCondition,
    },
    [POSITIONS.RIGHT]: {
      available: commonRightCondition && horizontalCenterCondition,
      verticalScrollOnly: horizontalCenterCondition,
    },
    [POSITIONS.RIGHT_END]: {
      available: commonRightCondition && horizontalEndCondition,
      verticalScrollOnly: horizontalEndCondition,
    },
    [POSITIONS.BOTTOM_START]: {
      available: commonBottomCondition && verticalStartCondition,
      verticalScrollOnly: verticalStartCondition,
    },
    [POSITIONS.BOTTOM]: {
      available: commonBottomCondition && verticalCenterCondition,
      verticalScrollOnly: verticalCenterCondition,
    },
    [POSITIONS.BOTTOM_END]: {
      available: commonBottomCondition && verticalEndCondition,
      verticalScrollOnly: verticalEndCondition,
    },
    [POSITIONS.LEFT_START]: {
      available: commonLeftCondition && horizontalStartCondition,
      verticalScrollOnly: horizontalStartCondition,
    },
    [POSITIONS.LEFT]: {
      available: commonLeftCondition && horizontalCenterCondition,
      verticalScrollOnly: horizontalCenterCondition,
    },
    [POSITIONS.LEFT_END]: {
      available: commonLeftCondition && horizontalEndCondition,
      verticalScrollOnly: horizontalEndCondition,
    },
  };
};

/**
 * Возвращает все выравнивания для переданной стороны
 *
 * @example getPositionsFromSide("bottom") -> ["bottom-start", "bottom-end", "bottom"]
 */
export const getAllSidePositions = (side: Side): Position[] => [
  `${side}-start`,
  `${side}-end`,
  side,
];

/**
 * Возвращает неповторяющиеся стороны из переданных позиций
 */
export const getUniqueSidesFromPositionPriorities = (positions: Position[]) => {
  const sides = positions.map((position) => position.split("-")[0] as Side);

  const uniqueSides = new Set(sides);

  return Array.from(uniqueSides);
};

/**
 * Возвращает позиции для переданных сторон, которых нет в переданных вторым аргументом позициях
 */
export const getOtherPositionsForSpecifiedSides = (
  sides: Side[],
  positionsPriorities: Position[]
) => {
  const allSidesPositions = sides
    .map((side) => getAllSidePositions(side))
    .flat();

  return allSidesPositions.filter(
    (position) => !positionsPriorities.includes(position)
  );
};

/**
 * Возвращает позиции, стороны которых присутствуют в списке переданных в параметрах, но выравнивания не соответствуют переданным
 *
 * @example getPreferableSidesAvailableAlignments({"bottom-start": true, "bottom-end": false}, ["bottom-end"]) -> ["bottom-start"]
 */
const getPreferableSidesAvailableAlignments = (
  availablePositions: Record<Position, boolean>,
  positionsPriorities: Position[]
) => {
  const uniqueSidesFromPositionsPriorities =
    getUniqueSidesFromPositionPriorities(positionsPriorities);

  return getOtherPositionsForSpecifiedSides(
    uniqueSidesFromPositionsPriorities,
    positionsPriorities
  ).filter((position) => availablePositions[position] === true);
};

/**
 * Возвращает доступную для отрисовки позицию
 */
export const getAvailablePosition = (
  positionsPriorities: Position[],
  { flyoutRect, parentRect, containerRect }: Rects
): Position => {
  const positionsConfig = getAvailablePositions({
    flyoutRect,
    parentRect,
    containerRect,
  });

  // если доступна позиция для отрисовки из списка переданных позиций - выбираем её
  const availablePosition = positionsPriorities.find(
    (position) => positionsConfig[position].available
  );

  if (availablePosition) {
    return availablePosition;
  }

  const availablePositions = Object.fromEntries(
    Object.entries(positionsConfig).map(
      ([position, { available }]) =>
        [position, available] as [Position, boolean]
    )
  ) as Record<Position, boolean>;

  // если доступна позиция, которой нет в списке переданных позиций, но она находится с той же стороны - выбираем её
  const preferableSidesAvailableAlignments =
    getPreferableSidesAvailableAlignments(
      availablePositions,
      positionsPriorities
    );

  if (preferableSidesAvailableAlignments.length > 0) {
    return preferableSidesAvailableAlignments[0];
  }

  const allPositionsForSidesFromPassedPositions = positionsPriorities.concat(
    getOtherPositionsForSpecifiedSides(
      getUniqueSidesFromPositionPriorities(positionsPriorities),
      positionsPriorities
    )
  );

  // если есть позиция, которая приведёт к появлению вертикального, но не горизонтального скролла,
  // выбираем первую доступную по приоритету - сначала переданные позиции, затем остальные
  const preferableFallbackPosition =
    allPositionsForSidesFromPassedPositions.find(
      (position) => positionsConfig[position].verticalScrollOnly
    );

  if (preferableFallbackPosition) {
    return preferableFallbackPosition;
  }

  // иначе возвращаем первую позицию из списка переданных
  return positionsPriorities[0];
};

/**
 * Примитивная реализация функции глубокого сравнения. Работает только с примитивами и массивами.
 */
export const deepEqual = (value1: unknown, value2: unknown): boolean => {
  if (typeof value1 !== typeof value2) {
    return false;
  }

  if (typeof value1 !== "object") {
    return value1 === value2;
  }

  if (Array.isArray(value1) && Array.isArray(value2)) {
    if (value1.length !== value2.length) {
      return false;
    }

    for (let i = 0; i < value1.length; i++) {
      if (value1[i] !== value2[i]) {
        return false;
      }
    }

    return true;
  }

  return false;
};

/**
 * Вычисляет координаты элемента относительно окна браузера (top, left всегда равны 0)
 */
export const getElementAbsoluteRects = (element: Element) => {
  const relativeRect = element.getBoundingClientRect();

  const { scrollX, scrollY } = window;

  const x = relativeRect.x + scrollX;
  const y = relativeRect.y + scrollY;

  const rect: DOMRectReadOnly = new DOMRectReadOnly(
    x,
    y,
    relativeRect.width,
    relativeRect.height
  );

  return rect;
};

export function getAlignment(placement: Position): Alignment {
  return (placement.split("-")[1] ?? "") as Alignment;
}

export function getSide(position: Position): Side {
  return position.split("-")[0] as Side;
}

export function getMainAxisFromSide(side: Side): Axis {
  return ["top", "bottom"].includes(side) ? "x" : "y";
}

export function getLengthFromAxis(axis: Axis): Length {
  return axis === "y" ? "height" : "width";
}

export function computeCoordinatesFromPlacement(
  { parent, flyout }: ElementRects,
  position: Position
): Coordinates {
  const commonX = parent.x + parent.width / 2 - flyout.width / 2;
  const commonY = parent.y + parent.height / 2 - flyout.height / 2;

  const side = getSide(position);

  let coordinates: Coordinates;

  switch (side) {
    case "top":
      coordinates = { x: commonX, y: parent.y - flyout.height };
      break;

    case "bottom":
      coordinates = { x: commonX, y: parent.y + parent.height };
      break;

    case "right":
      coordinates = { x: parent.x + parent.width, y: commonY };
      break;

    case "left":
      coordinates = { x: parent.x - flyout.width, y: commonY };
      break;

    default:
      coordinates = { x: parent.x, y: parent.y };
  }

  const mainAxis = getMainAxisFromSide(side);
  const length = getLengthFromAxis(mainAxis);

  switch (getAlignment(position)) {
    case "start":
      coordinates[mainAxis] = parent[mainAxis];
      break;

    case "end":
      coordinates[mainAxis] =
        parent[mainAxis] + parent[length] - flyout[length];
      break;

    default:
  }

  return coordinates;
}

/**
 * Корректирует координаты флайаута в зависимости от внутренних отступов родителя
 */
export const correctCoordinatesByPaddings = ({
  position,
  paddingBottom,
  paddingTop,
  paddingLeft,
  paddingRight,
  x,
  y,
}: {
  position: Position;
  paddingTop: string;
  paddingBottom: string;
  paddingRight: string;
  paddingLeft: string;
  x: number;
  y: number;
}) => {
  const parsedPaddingTop = parseInt(paddingTop, 10);
  const parsedPaddingBottom = parseInt(paddingBottom, 10);
  const parsedPaddingRight = parseInt(paddingRight, 10);
  const parsedPaddingLeft = parseInt(paddingLeft, 10);

  if (position.includes(POSITIONS.TOP) && parsedPaddingTop > 0) {
    return { x, y: y + parsedPaddingTop };
  }

  if (position.includes(POSITIONS.BOTTOM) && parsedPaddingBottom > 0) {
    return { x, y: y - parsedPaddingBottom };
  }

  if (position.includes(POSITIONS.RIGHT) && parsedPaddingRight > 0) {
    return { x: x - parsedPaddingRight, y };
  }

  if (position.includes(POSITIONS.LEFT) && parsedPaddingLeft > 0) {
    return { x: x + parsedPaddingLeft, y };
  }

  return { x, y };
};
